import {
  Container,
  Divider,
  Stack,
  Typography,
  Grid,
  Paper,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { themeOrange, themeBlue } from "../../utils/colorTheme";
import { useDispatch, useSelector } from "react-redux";
import { getAnalyticsAsync } from "../../services/AdminServiceManage";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const DashboardAnalytics = () => {
  const dispatch = useDispatch();
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await dispatch(getAnalyticsAsync()).unwrap();
        setAnalytics(data);
      } catch (e) {
        console.log("analytics fetch failed:", e?.message);
      }
    })();
  }, [dispatch]);

  const ordersByStatus = analytics?.orders_by_status || {};
  const ug = analytics?.user_growth || {};
  const mo = analytics?.monthly_orders || {};
  const sp = analytics?.service_popularity || {};

  // Orders by Status Chart Data (real if loaded, fallback zeros)
  const ordersStatusData = {
    labels: ["Completed", "Pending", "In Progress", "Cancelled"],
    datasets: [
      {
        data: [
          ordersByStatus.completed ?? 0,
          ordersByStatus.pending ?? 0,
          ordersByStatus.in_progress ?? 0,
          ordersByStatus.cancelled ?? 0,
        ],
        backgroundColor: ["#22c55e", themeOrange, themeBlue, "#ef4444"],
        borderWidth: 0,
      },
    ],
  };

  // User Growth Chart Data — last 12 months from backend
  const userGrowthData = {
    labels: ug.labels || [],
    datasets: [
      {
        label: "Customers",
        data: ug.customers || [],
        borderColor: themeBlue,
        backgroundColor: "rgba(4, 61, 119, 0.1)",
        fill: true,
        tension: 0.4,
      },
      {
        label: "Pros",
        data: ug.vendors || [],
        borderColor: themeOrange,
        backgroundColor: "rgba(241, 90, 36, 0.1)",
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Monthly Orders (last 6 months)
  const monthlyOrdersData = {
    labels: mo.labels || [],
    datasets: [
      {
        label: "Orders",
        data: mo.counts || [],
        backgroundColor: themeBlue,
        borderRadius: 8,
      },
    ],
  };

  // Service Popularity — top 6 real services
  const spPalette = [themeOrange, themeBlue, "#22c55e", "#8b5cf6", "#ec4899", "#6b7280"];
  const servicePopularityData = {
    labels: sp.labels || [],
    datasets: [
      {
        label: "Bookings",
        data: sp.counts || [],
        backgroundColor: (sp.labels || []).map((_, i) => spPalette[i % spPalette.length]),
        borderRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  const barOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Stack bgcolor={"#FAFAFA"}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}
        my={1}
      >
        <Typography className="mainHeading">Analytics</Typography>
      </Stack>
      <Divider />

      <Container
        sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" } }}
      >
        <Stack my={3}>
          <Grid container spacing={3}>
            {/* Orders by Status */}
            <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                  height: "350px",
                }}
              >
                <Typography
                  className="mainHeading"
                  sx={{ fontSize: "18px !important", mb: 2 }}
                >
                  Orders by Status
                </Typography>
                <Stack sx={{ height: "270px" }}>
                  <Doughnut data={ordersStatusData} options={chartOptions} />
                </Stack>
              </Paper>
            </Grid>

            {/* User Growth */}
            <Grid item xl={8} lg={8} md={6} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                  height: "350px",
                }}
              >
                <Typography
                  className="mainHeading"
                  sx={{ fontSize: "18px !important", mb: 2 }}
                >
                  User & Pro Growth
                </Typography>
                <Stack sx={{ height: "270px" }}>
                  <Line data={userGrowthData} options={chartOptions} />
                </Stack>
              </Paper>
            </Grid>

            {/* Monthly Orders */}
            <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                  height: "350px",
                }}
              >
                <Typography
                  className="mainHeading"
                  sx={{ fontSize: "18px !important", mb: 2 }}
                >
                  Monthly Orders
                </Typography>
                <Stack sx={{ height: "270px" }}>
                  <Bar data={monthlyOrdersData} options={barOptions} />
                </Stack>
              </Paper>
            </Grid>

            {/* Service Popularity */}
            <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  boxShadow: "0px 4px 10px 0px #00000026",
                  height: "350px",
                }}
              >
                <Typography
                  className="mainHeading"
                  sx={{ fontSize: "18px !important", mb: 2 }}
                >
                  Service Popularity
                </Typography>
                <Stack sx={{ height: "270px" }}>
                  <Bar data={servicePopularityData} options={barOptions} />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Stack>
  );
};

export default DashboardAnalytics;
